// FILE: src/services/prepTasksService.js
//
// Exam Prep checklist — self-attested, toggleable prep milestones, distinct
// from the auto-earned achievements in achievementService.
//
// Backed by `learner_prep_tasks` (migration 025): ownership-keyed, row-present
// = complete. Marking a task complete inserts a row (user_id defaults to
// auth.uid()); un-checking deletes it. RLS restricts to own rows.
//
// The catalog (keys, certs, labels, due offsets, hour targets) lives here —
// it's small and stable. Due dates are computed from the learner's
// target_exam_date (passed in by the caller, which has the profile).

import { supabase } from './supabaseClient';

// ── Catalog ──────────────────────────────────────────────────────────────────
// dueOffsetDays = recommended completion this many days BEFORE the target exam
// date (null = no deadline). `hours` is the required training-hour target per
// cert (display only — the task is a single self-attested checkbox).
export const PREP_TASKS = [
  {
    key:   'training_hours',
    certs: ['pmp', 'cpmai', 'pmi-pmocp'],
    title: 'Complete required training hours',
    hours: { pmp: 35, cpmai: 25, 'pmi-pmocp': 10 },
    dueOffsetDays: null,
    description: (examId) =>
      `Log the ${({ pmp: 35, cpmai: 25, 'pmi-pmocp': 10 })[examId] || 35} contact/training hours your certification requires.`,
  },
  {
    key:   'application',
    certs: ['pmp'],
    title: 'Submit your PMP application',
    dueOffsetDays: 21, // at least three weeks before the exam
    description: () =>
      'Cite your project experience and education on the PMI application — allow time for review.',
  },
  {
    key:   'schedule_exam',
    certs: ['pmp'],
    title: 'Schedule your exam',
    dueOffsetDays: 3, // book by a few days before the target date
    description: () =>
      'Book your exam appointment at a test center or online proctor.',
  },
];

/** Task definitions applicable to a given cert. */
export function prepTasksForExam(examId) {
  return PREP_TASKS.filter((t) => t.certs.includes(examId));
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// Recommended-by date (target exam date minus the offset) + overdue flag.
function dueInfo(task, targetExamDate, completed) {
  if (task.dueOffsetDays == null) return { dueDate: null, overdue: false, needsTargetDate: false };
  if (!targetExamDate) return { dueDate: null, overdue: false, needsTargetDate: true };
  try {
    const due = new Date(targetExamDate);
    due.setUTCDate(due.getUTCDate() - task.dueOffsetDays);
    const dueDate = due.toISOString().slice(0, 10);
    return { dueDate, overdue: !completed && todayIso() > dueDate, needsTargetDate: false };
  } catch (_) {
    return { dueDate: null, overdue: false, needsTargetDate: false };
  }
}

/**
 * Load the prep tasks for a cert, merged with the learner's completion state
 * and computed due/overdue info.
 *
 * @param {string} examId
 * @param {object} [opts]
 * @param {string|null} [opts.targetExamDate] ISO date the exam is targeted for
 * @returns {Promise<Array<{key,title,description,hours,completed,dueDate,overdue,needsTargetDate}>>}
 */
export async function loadPrepTasks(examId, { targetExamDate = null } = {}) {
  const defs = prepTasksForExam(examId);
  if (defs.length === 0) return [];

  let completedKeys = new Set();
  try {
    const { data, error } = await supabase
      .from('learner_prep_tasks')
      .select('task_key')
      .eq('exam_id', examId);
    if (!error && Array.isArray(data)) {
      completedKeys = new Set(data.map((r) => r.task_key));
    }
  } catch (_) {
    // Not signed in / transient — treat everything as incomplete.
  }

  return defs.map((t) => {
    const completed = completedKeys.has(t.key);
    return {
      key:         t.key,
      title:       t.title,
      description: t.description(examId),
      hours:       t.hours ? t.hours[examId] : null,
      completed,
      ...dueInfo(t, targetExamDate, completed),
    };
  });
}

/**
 * Mark a prep task complete (insert) or not (delete). user_id defaults to
 * auth.uid() on insert; RLS scopes the delete to the caller's row.
 *
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function setPrepTaskComplete(taskKey, examId, complete) {
  try {
    if (complete) {
      const { error } = await supabase
        .from('learner_prep_tasks')
        .insert({ task_key: taskKey, exam_id: examId });
      // A unique-violation just means it was already complete — not an error.
      if (error && !/duplicate|unique|23505/i.test(error.message || error.code || '')) {
        console.warn('[prepTasksService] mark complete failed:', error.message);
        return { ok: false, error: error.message };
      }
    } else {
      const { error } = await supabase
        .from('learner_prep_tasks')
        .delete()
        .eq('task_key', taskKey)
        .eq('exam_id', examId);
      if (error) {
        console.warn('[prepTasksService] un-mark failed:', error.message);
        return { ok: false, error: error.message };
      }
    }
    return { ok: true };
  } catch (err) {
    console.warn('[prepTasksService] unexpected error:', err?.message || err);
    return { ok: false, error: err?.message || String(err) };
  }
}
